/**
 * Which failed MUTATING requests the axios layer retries (with backoff): a network error, a server fault (5xx), and
 * the one 409 that means "the same request is still running" - the idempotency layer's REQUEST_IN_PROGRESS. Every
 * other 409 is a conflict the caller has to resolve (a duplicate name, a game in the wrong state, an invitation
 * already used); retrying it three times only delayed the message. Older servers sent no code on the in-progress
 * 409, so its idempotencyKey still marks it.
 */
export const REQUEST_IN_PROGRESS = 'REQUEST_IN_PROGRESS';

const CANCELED = 'ERR_CANCELED';
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

/**
 * Which failed GETs are worth sending again: the request reached no server (dropped connection, the pod went away
 * mid-deploy) or the gateway had nothing behind it (502/503/504). Not a timeout - a read that ran 90s already had its
 * chance, and a retry would only triple the wait - and not a cancellation, which the caller asked for.
 */
export const isTransientReadFailure = (error) => {
    if (!error || error.code === CANCELED || TIMEOUT_CODES.includes(error.code)) return false;
    if (!error.response) return true;
    return [502, 503, 504].includes(error.response.status);
};

/**
 * The request never got to the app: no answer at all, or the load balancer had nothing to send it to (502). Unlike a
 * 503 or 504 from one slow feature, this means the whole app is out of reach - the "Can't reach GeoPlay" bar.
 */
export const reachedNoServer = (error) => {
    if (!error || error.code === CANCELED || TIMEOUT_CODES.includes(error.code)) return false;
    if (!error.response) return true;
    return error.response.status === 502;
};

export const isRetryableFailure = (error) => {
    if (!error?.response) return true; // network error
    const { status, data } = error.response;
    if (status >= 500 && status < 600) return true;
    if (status === 409) return data?.code === REQUEST_IN_PROGRESS || !!data?.idempotencyKey;
    return false;
};
